import * as React from "react";

import * as _ from "lodash";
import { ImageUrls } from "shared/src/types/articlesearch/Article";

type Props = {
    onSuccessfulLoad: (str: string) => void;
    src: ImageUrls;
    defaultWidth?: number;
    defaultHeight?: number;
    imgClassName?: string;
    description?: string;
    breakPointConfigs: Array<{
        lowerBreakpoint?: number;
        upperBreakpoint?: number;
        imageWidth: number;
        imageHeight: number;
    }>;
};

class BundleImage extends React.Component<
    Props,
    {
        urlObject: {
            url: string;
            urlType: string;
        };
        urls: Array<{
            url: string;
            urlType: string;
        }>;
    }
> {
    constructor(props: Props) {
        super(props);

        const urls = this.buildUrls(props);
        this.state = this.getNewImageUrlCandidate(urls);
    }

    onSuccess() {
        this.props.onSuccessfulLoad(this.state.urlObject.urlType);
    }

    buildUrls(props: Props): Array<{
        url: string;
        urlType: string;
    }> {
        const urls = [];
        urls.push({
            url: "/ordercapture/uidispatcher/static/app/assets/images/placeholder_food_item.png",
            urlType: "fallback"
        }); // White gif as FallBack.
        if (props.src.bundleFallBack) {
            urls.push({
                url: props.src.bundleFallBack,
                urlType: "bundleFallback"
            });
        }
        if (props.src.variant) {
            urls.push({
                url: props.src.variant,
                urlType: "variant"
            });
        }
        if (props.src.bundle) {
            urls.push({
                url: props.src.bundle,
                urlType: "bundle"
            });
        }

        // remove all duplicate elements, because otherwise our onError logic does not work (caching). If two urls
        // are the same and both lead to an error, the onError handler would only be called once.
        return _.uniqBy(urls, "url");
    }

    componentWillReceiveProps(nextProps: Props) {
        if (!_.isEqual(nextProps.src, this.props.src)) {
            const urls = this.buildUrls(nextProps);
            this.setState(this.getNewImageUrlCandidate(urls));
        }
    }

    getNewImageUrlCandidate(
        urls: Array<{
            url: string;
            urlType: string;
        }>
    ) {
        const newUrls = urls.slice(0, urls.length - 1 || 1); // Always keep the default image
        return { urlObject: urls[urls.length - 1], urls: newUrls };
    }

    loadNext() {
        this.setState(this.getNewImageUrlCandidate(this.state.urls));
    }

    appendWidthAndHeightQueryParams(url: string, width: number, height: number) {
        const prefix = _.includes(url, "?") ? "&" : "?";
        return url + prefix + "w=" + width + "&h=" + height;
    }

    getMediaQuery(config: { lowerBreakpoint?: number; upperBreakpoint?: number }) {
        const lowerSetting = config.lowerBreakpoint ? "(min-width: " + config.lowerBreakpoint + "px)" : undefined;
        const upperSetting = config.upperBreakpoint ? "(max-width: " + config.upperBreakpoint + "px)" : undefined;

        return _.chain([lowerSetting, upperSetting]).compact().join(" and ").value();
    }

    render() {
        return (
            <picture onLoad={() => this.onSuccess()}>
                {_.map(this.props.breakPointConfigs, (config, index: number) => {
                    return (
                        <source
                            key={index}
                            media={this.getMediaQuery(config)}
                            srcSet={this.appendWidthAndHeightQueryParams(
                                this.state.urlObject.url,
                                config.imageWidth,
                                config.imageHeight
                            )}
                        />
                    );
                })}
                <img
                    src={this.appendWidthAndHeightQueryParams(
                        this.state.urlObject.url,
                        this.props.defaultWidth || 144,
                        this.props.defaultHeight || 144
                    )}
                    loading="lazy"
                    alt={this.props.description}
                    onError={() => this.loadNext()}
                    className={this.props.imgClassName || "img-responsive"}
                />
            </picture>
        );
    }
}

export default BundleImage;
